import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Asset, AssetCategory, AssetStatus } from '../../types';

const useStyles = makeStyles(_theme => ({
  dialog: {
    minWidth: 600,
  },
  formControl: {
    width: '100%',
  },
}));

interface AddAssetDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (asset: Partial<Asset>) => void;
}

export const AddAssetDialog: React.FC<AddAssetDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<Partial<Asset>>({
    assetTag: '',
    name: '',
    category: AssetCategory.HARDWARE,
    type: '',
    status: AssetStatus.ACTIVE,
    owner: 'MOH ICT',
    assignedTo: '',
    location: '',
    department: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchaseCost: 0,
    currency: 'USD',
    vendor: '',
    warrantyExpiry: '',
    serialNumber: '',
    model: '',
    manufacturer: '',
    licenseKey: '',
    licenseExpiry: '',
    maintenanceSchedule: '',
    notes: '',
  });

  const handleChange =
    (field: keyof Asset) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
    };

  const handleSubmit = () => {
    onSave({
      ...formData,
      id: `asset-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setFormData({
      assetTag: '',
      name: '',
      category: AssetCategory.HARDWARE,
      type: '',
      status: AssetStatus.ACTIVE,
      owner: 'MOH ICT',
      assignedTo: '',
      location: '',
      department: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchaseCost: 0,
      currency: 'USD',
      vendor: '',
      warrantyExpiry: '',
      serialNumber: '',
      model: '',
      manufacturer: '',
      licenseKey: '',
      licenseExpiry: '',
      maintenanceSchedule: '',
      notes: '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Asset</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="Asset Tag"
              value={formData.assetTag}
              onChange={handleChange('assetTag')}
              placeholder="e.g., MOH-HW-001"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label="Asset Name"
              value={formData.name}
              onChange={handleChange('name')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={handleChange('category')}
              >
                <MenuItem value={AssetCategory.HARDWARE}>Hardware</MenuItem>
                <MenuItem value={AssetCategory.SOFTWARE}>Software</MenuItem>
                <MenuItem value={AssetCategory.NETWORK}>Network</MenuItem>
                <MenuItem value={AssetCategory.INFRASTRUCTURE}>
                  Infrastructure
                </MenuItem>
                <MenuItem value={AssetCategory.MOBILE_DEVICE}>
                  Mobile Device
                </MenuItem>
                <MenuItem value={AssetCategory.PERIPHERALS}>
                  Peripherals
                </MenuItem>
                <MenuItem value={AssetCategory.LICENSE}>License</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Type"
              value={formData.type}
              onChange={handleChange('type')}
              placeholder="e.g., Server, Laptop, License"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Status</InputLabel>
              <Select value={formData.status} onChange={handleChange('status')}>
                <MenuItem value={AssetStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={AssetStatus.INACTIVE}>Inactive</MenuItem>
                <MenuItem value={AssetStatus.IN_MAINTENANCE}>
                  In Maintenance
                </MenuItem>
                <MenuItem value={AssetStatus.RETIRED}>Retired</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Owner"
              value={formData.owner}
              onChange={handleChange('owner')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Assigned To"
              value={formData.assignedTo}
              onChange={handleChange('assignedTo')}
              placeholder="Person or department"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={handleChange('location')}
              placeholder="Physical location"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Department"
              value={formData.department}
              onChange={handleChange('department')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Purchase Date"
              type="date"
              value={formData.purchaseDate}
              onChange={handleChange('purchaseDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Purchase Cost"
              type="number"
              value={formData.purchaseCost}
              onChange={handleChange('purchaseCost')}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControl className={classes.formControl}>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={handleChange('currency')}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZMW">ZMW</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Vendor"
              value={formData.vendor}
              onChange={handleChange('vendor')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={handleChange('manufacturer')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Model"
              value={formData.model}
              onChange={handleChange('model')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Serial Number"
              value={formData.serialNumber}
              onChange={handleChange('serialNumber')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Warranty Expiry"
              type="date"
              value={formData.warrantyExpiry}
              onChange={handleChange('warrantyExpiry')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Key"
              value={formData.licenseKey}
              onChange={handleChange('licenseKey')}
              placeholder="For software licenses"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="License Expiry"
              type="date"
              value={formData.licenseExpiry}
              onChange={handleChange('licenseExpiry')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl}>
              <InputLabel>Maintenance Schedule</InputLabel>
              <Select
                value={formData.maintenanceSchedule}
                onChange={handleChange('maintenanceSchedule')}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Quarterly">Quarterly</MenuItem>
                <MenuItem value="Annually">Annually</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={3}
              placeholder="Additional information..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!formData.assetTag || !formData.name}
        >
          Save Asset
        </Button>
      </DialogActions>
    </Dialog>
  );
};
